import { doc, onSnapshot, type DocumentData } from "firebase/firestore";
import { appConfigDb } from "./firebase";

export type KioskRemoteLoginCommand = {
  schemaVersion: 1;
  environment: "production" | "staging";
  commandId: string;
  incidentId: string | null;
  apply: true;
  expiresAtMs: number;
  kioskInstallationId: string;
  storeKey: string;
  zoneId: string;
  placement: "enter" | "exit";
  accessToken: string;
  refreshToken: string;
};

export type KioskRemoteLoginSource = {
  subscribe(listener: () => void): () => void;
  getSnapshot(): KioskRemoteLoginCommand | null;
};

export type InMemoryKioskRemoteLoginSource = KioskRemoteLoginSource & {
  publish(command: KioskRemoteLoginCommand | null): void;
};

function nonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function toMillis(value: unknown): number | null {
  if (
    value &&
    typeof (value as { toMillis?: () => number }).toMillis === "function"
  ) {
    const millis = (value as { toMillis: () => number }).toMillis();
    return Number.isFinite(millis) ? millis : null;
  }
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function normalizeRemoteLoginCommand(
  data: DocumentData,
): KioskRemoteLoginCommand | null {
  const expiresAtMs = toMillis(data.expiresAt);
  const environment = data.environment;
  const incidentId = data.incidentId;
  const placement = data.placement;

  if (
    data.schemaVersion !== 1 ||
    data.apply !== true ||
    (environment !== "production" && environment !== "staging") ||
    !nonEmptyString(data.commandId) ||
    (incidentId != null && !nonEmptyString(incidentId)) ||
    expiresAtMs == null ||
    !nonEmptyString(data.kioskInstallationId) ||
    !nonEmptyString(data.storeKey) ||
    !nonEmptyString(data.zoneId) ||
    (placement !== "enter" && placement !== "exit") ||
    !nonEmptyString(data.accessToken) ||
    !nonEmptyString(data.refreshToken)
  ) {
    return null;
  }

  return {
    schemaVersion: 1,
    environment,
    commandId: data.commandId,
    incidentId: incidentId ?? null,
    apply: true,
    expiresAtMs,
    kioskInstallationId: data.kioskInstallationId,
    storeKey: data.storeKey,
    zoneId: data.zoneId,
    placement,
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
  };
}

export function createFirestoreRemoteLoginSource(
  kioskInstallationId: string,
): KioskRemoteLoginSource {
  let current: KioskRemoteLoginCommand | null = null;
  let unsubscribe: (() => void) | null = null;
  const listeners = new Set<() => void>();

  const start = () => {
    unsubscribe = onSnapshot(
      doc(appConfigDb, "remote_login", kioskInstallationId),
      (snapshot) => {
        current = snapshot.exists()
          ? normalizeRemoteLoginCommand(snapshot.data())
          : null;
        listeners.forEach((listener) => listener());
      },
      () => {
        // Remote login is optional recovery infrastructure. Listener failures
        // leave the kiosk in its existing signed-out state and must not expose
        // the credential-bearing snapshot through logs.
        current = null;
        listeners.forEach((listener) => listener());
      },
    );
  };

  return {
    getSnapshot: () => current,
    subscribe(listener) {
      listeners.add(listener);
      if (listeners.size === 1) start();

      return () => {
        listeners.delete(listener);
        if (listeners.size === 0 && unsubscribe) {
          unsubscribe();
          unsubscribe = null;
        }
      };
    },
  };
}

export function createInMemoryRemoteLoginSource(
  initial: KioskRemoteLoginCommand | null = null,
): InMemoryKioskRemoteLoginSource {
  let current = initial;
  const listeners = new Set<() => void>();

  return {
    getSnapshot: () => current,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    publish(command) {
      if (command === current) return;
      current = command;
      listeners.forEach((listener) => listener());
    },
  };
}
